/* eslint-disable formatjs/no-literal-string-in-jsx */

import React, { FunctionComponent, ReactElement, useEffect, useRef, useState } from 'react'

import styled from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { IS_WEB } from '../../constants'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type TableOfContentsProps = {
  clickedTOCHeaders?: string[]
  containerPadding?: number
  headers: { elementId: string; title: string; badges?: ReactElement[] }[]
  setClickedTOCHeaders?: (clickedTOCHeaders: string[]) => void
  testID?: string
}

const Container = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['16px'],
  display: 'flex',
  flexDirection: 'column',
}))

const SectionItemContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing['4px'],
  alignItems: 'center',
}))

const NavItemButtonActive = styled.View<{ theme: ThemeType; containerPadding?: number }>(
  ({ theme, containerPadding = 16 }) => ({
    width: '4px',
    height: '100%',
    backgroundColor: theme.colors.backgroundActive,
    position: 'absolute',
    borderTopRightRadius: theme.spacing['16px'],
    borderBottomRightRadius: theme.spacing['16px'],
    left: `-${containerPadding}px`,
    right: 0,
    bottom: '0',
    marginTop: 'auto',
    marginBottom: 'auto',
  }),
)

const SectionText = styled(BodyText)<{ theme: ThemeType; active: boolean }>(({ theme, active }) => ({
  color: active ? theme.colors.textActive : theme.colors.textSecondary,
}))

const BadgesContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: spacing['8px'],
  gap: spacing['8px'],
  display: 'flex',
  flexDirection: 'row',
}))

const STICKY_HEADER_CLEARANCE = 160
const SCROLL_SETTLE_MS = 150
const SCROLL_BOTTOM_SLOP = 8

const findSection = ({ elementId, title }: { elementId: string; title: string }): HTMLElement | null =>
  document.getElementById(elementId) ??
  Array.from(document.querySelectorAll<HTMLElement>('[role="heading"]')).find((el) => el.textContent === title) ??
  null

const findScrollRoot = (section: HTMLElement | null): HTMLElement | null => {
  for (let el = section?.parentElement; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el)
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      return el
    }
  }
  return null
}

export const TableOfContents: FunctionComponent<TableOfContentsProps> = ({
  clickedTOCHeaders = [],
  containerPadding,
  headers,
  setClickedTOCHeaders,
  testID = 'TableOfContents',
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const clickedIndex = useRef<number | null>(null)

  useEffect(() => {
    if (!IS_WEB || headers.length === 0) {
      return
    }

    const updateActiveSection = () => {
      const sections = headers.map(findSection)
      const root = findScrollRoot(sections.find(Boolean) ?? null)
      const scroller = root ?? document.documentElement
      const activationLine = (root?.getBoundingClientRect().top ?? 0) + STICKY_HEADER_CLEARANCE
      const atBottom =
        scroller.scrollHeight > scroller.clientHeight + SCROLL_BOTTOM_SLOP &&
        scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - SCROLL_BOTTOM_SLOP
      let nextIndex = atBottom ? headers.length - 1 : 0
      if (!atBottom) {
        sections.forEach((section, i) => {
          if (section && section.getBoundingClientRect().top <= activationLine + 1) {
            nextIndex = i
          }
        })
      }
      if (clickedIndex.current !== null) {
        if (nextIndex !== clickedIndex.current) {
          return
        }
        clickedIndex.current = null
      }
      setActiveIndex(nextIndex)
    }

    let settleTimer: number | undefined
    const armSettleTimer = () => {
      window.clearTimeout(settleTimer)
      settleTimer = window.setTimeout(() => {
        clickedIndex.current = null
        updateActiveSection()
      }, SCROLL_SETTLE_MS)
    }
    const onScroll = ({ target }: Event) => {
      if (target instanceof HTMLElement && !headers.some((header) => target.contains(findSection(header)))) {
        return
      }
      armSettleTimer()
      updateActiveSection()
    }

    updateActiveSection()
    if (clickedIndex.current !== null) {
      armSettleTimer()
    }
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true })
      window.clearTimeout(settleTimer)
    }
  }, [headers])

  const sectionClicked = (header: TableOfContentsProps['headers'][number], i: number) => {
    setActiveIndex(i)
    setClickedTOCHeaders?.([...new Set(clickedTOCHeaders).add(header.title)])
    const section = IS_WEB ? findSection(header) : null
    if (!section) {
      return
    }
    const root = findScrollRoot(section)
    const top = section.getBoundingClientRect().top - (root?.getBoundingClientRect().top ?? 0) - STICKY_HEADER_CLEARANCE
    if (Math.abs(top) < 1) {
      return
    }
    clickedIndex.current = i
    ;(root ?? window).scrollBy({ top, behavior: 'smooth' })
  }

  return (
    <Container testID={tID(`${testID}`)}>
      {headers.map((header, i) => {
        return (
          <SectionItemContainer testID={tID(`${testID}-${header.elementId}`)} key={header.elementId}>
            {activeIndex === i && (
              <NavItemButtonActive containerPadding={containerPadding} testID={tID(`Header-active`)} />
            )}
            <SectionText
              size={BodyTextSize.SMALL}
              text={header.title}
              active={activeIndex === i}
              onPress={() => sectionClicked(header, i)}
              testID={tID(`${testID}-${header.elementId}-text`)}
            />
            <BadgesContainer testID={tID(`${testID}-${header.elementId}-badgeContainer`)}>
              {header.badges}
            </BadgesContainer>
          </SectionItemContainer>
        )
      })}
    </Container>
  )
}
